import { FunctionComponent, useState } from 'react'
import { Accessibility, AccessibilityType, Daycare } from '@/types/Daycare'
import styles from './AccessibilitiesLists.module.scss'
import { Table, TableBody, TableCell, TableRow } from '@mui/material'
import AccessibilitiesListsRow from './AccessibilitiesListsRow'
import Title from '@/components/_custom/Title/title'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useTranslation } from 'react-i18next'
import AccessibilityDialog from './Dialog/accessibilityDialog'
import moment from 'moment/moment'

interface AccessibilitiesListsProps {
    daycare: Daycare
    type: AccessibilityType
    disabled: boolean
    canUpdate: boolean
}

const AccessibilitiesLists: FunctionComponent<AccessibilitiesListsProps> = ({
    daycare,
    type,
    disabled,
    canUpdate
}: AccessibilitiesListsProps) => {
    const { t } = useTranslation()

    const NUMBER_OF_ACCESSIBILITIES: number = 4

    const [isAccessibilityDialogOpen, setIsAccessibilityDialogOpen] = useState<boolean>(false)

    const [filteredAccessibility, setFilteredAccessibility] = useState<Accessibility[]>(
        [...(daycare.accessibility || [])]
            .filter((accessibility: Accessibility): boolean => accessibility.type === type)
            .sort((a: Accessibility, b: Accessibility): number =>
                moment(a.start, 'HH:mm').format('HH:mm') > moment(b.start, 'HH:mm').format('HH:mm') ? 1 : -1
            )
    )

    function removeOneAccessibility(accessibilityId: number): void {
        const newFilteredAccessibility: Accessibility[] = filteredAccessibility.filter(
            (accessibility: Accessibility): boolean => accessibility.id !== accessibilityId
        )
        setFilteredAccessibility([])
        setFilteredAccessibility(
            [...newFilteredAccessibility].sort((a: Accessibility, b: Accessibility): number =>
                moment(a.start, 'HH:mm').format('HH:mm') > moment(b.start, 'HH:mm').format('HH:mm') ? 1 : -1
            )
        )
    }

    function addOneAccessibility(accessibility: Accessibility): void {
        const newAccessibilities: Accessibility[] = [...filteredAccessibility, accessibility].sort(
            (a: Accessibility, b: Accessibility): number =>
                moment(a.start, 'HH:mm').format('HH:mm') > moment(b.start, 'HH:mm').format('HH:mm') ? 1 : -1
        )
        setFilteredAccessibility([])
        setFilteredAccessibility(newAccessibilities)
    }

    function updateOneAccessibility(accessibility: Accessibility): void {
        const accessibilities: Accessibility[] = filteredAccessibility.filter(
            (a: Accessibility): boolean => a.id !== accessibility.id
        )
        setFilteredAccessibility([])
        setFilteredAccessibility(
            [...accessibilities, accessibility].sort((a: Accessibility, b: Accessibility): number =>
                moment(a.start, 'HH:mm').format('HH:mm') > moment(b.start, 'HH:mm').format('HH:mm') ? 1 : -1
            )
        )
    }

    return (
        <div className={styles.AccessibilitiesPlacement}>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <Title
                        text={type === AccessibilityType.arrival ? t('myArrivals') : t('myDepartures')}
                        className={styles.Title}
                    />
                    {canUpdate && (
                        <ButtonTextIcon
                            type={'add'}
                            className={styles.Button}
                            onClick={() => setIsAccessibilityDialogOpen(true)}
                            disabled={filteredAccessibility.length >= NUMBER_OF_ACCESSIBILITIES && !disabled}
                        />
                    )}
                </div>
                <Table className={styles.TableComponent}>
                    <TableBody>
                        {filteredAccessibility.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3}>
                                    {type === AccessibilityType.arrival ? t('noArrivals') : t('noDepartures')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            [...filteredAccessibility]
                                .sort((a: Accessibility, b: Accessibility): number =>
                                    moment(a.start, 'HH:mm').format('HH:mm') > moment(b.start, 'HH:mm').format('HH:mm')
                                        ? 1
                                        : -1
                                )
                                .map((accessibility: Accessibility, index: number) => (
                                    <AccessibilitiesListsRow
                                        key={
                                            accessibility.id.toString() +
                                            accessibility.start +
                                            accessibility.end +
                                            index
                                        }
                                        daycare={daycare}
                                        accessibility={accessibility}
                                        accessibilities={filteredAccessibility}
                                        removeOneAccessibility={removeOneAccessibility}
                                        canUpdate={canUpdate}
                                        updateOneAccessibility={updateOneAccessibility}
                                    />
                                ))
                        )}
                    </TableBody>
                </Table>
                <AccessibilityDialog
                    daycare={daycare}
                    accessibilityType={type}
                    create={true}
                    isDialogOpen={isAccessibilityDialogOpen}
                    handleCloseDialog={() => setIsAccessibilityDialogOpen(false)}
                    addOneAccessibility={addOneAccessibility}
                    accessibilities={filteredAccessibility}
                />
            </div>
        </div>
    )
}

export default AccessibilitiesLists
